<script lang="ts">
  import { myTrees, createTree, go, deleteTree } from '../lib/stores';
  import { authState } from '../lib/auth';
  import { parseGedcom } from '../lib/gedcom';
  import { db } from '../lib/db';
  import { logAudit } from '../lib/audit';
  import MiniTree from './MiniTree.svelte';

  let newTitle = '';
  let creating = false;
  let importInput: HTMLInputElement;
  let importPhase: 'idle' | 'parsing' | 'error' = 'idle';
  let importMessage = '';
  let importDetail = '';

  async function onCreate() {
    if (!newTitle.trim()) newTitle = 'Untitled tree';
    creating = true;
    try {
      const t = await createTree(newTitle);
      newTitle = '';
      go(`/tree/${t.id}`);
    } finally { creating = false; }
  }

  async function onDelete(id: string, title: string) {
    if (!confirm(`Delete "${title}"? Shared copies are not affected.`)) return;
    await deleteTree(id);
  }

  async function onImport(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    importPhase = 'parsing';
    importMessage = `parsing ${file.name}…`;
    importDetail = '';
    try {
      const raw = await file.text();
      // Provisional treeId; a real one is created below and patched into records.
      const provisionalTreeId = 'provisional';
      const result = parseGedcom(raw, provisionalTreeId);
      // eslint-disable-next-line no-console
      console.log('[GEDCOM import via Home]', {
        file: file.name, bytes: file.size, ...result.stats,
        people: result.people.length, rels: result.relationships.length,
        warnings: result.warnings,
      });
      if (result.people.length === 0) {
        importPhase = 'error';
        importMessage = 'No individuals found in this GEDCOM';
        importDetail = result.warnings.join(' ') ||
          `Parsed ${result.stats.totalLines} GEDCOM lines but extracted 0 records. Diagnostics in the browser console.`;
        input.value = '';
        return;
      }
      const title = file.name.replace(/\.(ged|gedcom|txt)$/i, '') || 'Imported tree';
      const tree = await createTree(title);
      // Re-stamp records to the real tree id.
      for (const p of result.people) p.treeId = tree.id;
      for (const r of result.relationships) r.treeId = tree.id;
      await db.transaction('rw', db.people, db.relationships, async () => {
        await db.people.bulkAdd(result.people);
        await db.relationships.bulkAdd(result.relationships);
      });
      await logAudit('merge.apply', {
        treeId: tree.id,
        meta: {
          source: 'gedcom-import',
          file: file.name,
          added: { people: result.people.length, rels: result.relationships.length },
          stats: result.stats,
        },
      });
      input.value = '';
      go(`/tree/${tree.id}`);
    } catch (e) {
      importPhase = 'error';
      importMessage = 'Import failed';
      importDetail = (e as Error).message ?? String(e);
      // eslint-disable-next-line no-console
      console.error('[GEDCOM import via Home] failed', e);
      input.value = '';
    }
  }

  function handle(email: string | null): string {
    if (!email) return 'friend';
    return email.split('@')[0];
  }

  function short(id: string): string { return id.slice(0, 8); }
</script>

<div class="wrap">
  <header class="head enter">
    <div class="row meta">
      <span class="chip">user.{handle($authState.email)}</span>
      <span class="chip">role.{$authState.profile?.role ?? 'user'}</span>
      <span class="chip signal">ready</span>
    </div>
    <h1>Pick up a tree,<br/>or <em>plant a new one</em>.</h1>
    <p class="lede">
      Offline-first. Nothing leaves your device until you share it. Import a
      GEDCOM to hit the ground running.
    </p>
  </header>

  <section class="grid">
    <div class="card create enter">
      <div class="row head-row">
        <h3>New tree</h3>
        <span class="chip">init</span>
      </div>
      <p class="dim small">Give it a name — rename later.</p>
      <form on:submit|preventDefault={onCreate} class="stack">
        <div class="field">
          <label for="title">title</label>
          <input id="title" placeholder="Nel family tree" bind:value={newTitle} />
        </div>
        <button class="primary" type="submit" disabled={creating}>
          {creating ? 'creating…' : 'create →'}
        </button>
      </form>

      <div class="rule-thin"></div>

      <div class="stack">
        <p class="dim small">…or jump-start from a GEDCOM file.</p>
        <input type="file" accept=".ged,.gedcom,text/plain" bind:this={importInput}
               on:change={onImport} hidden />
        <button class="" on:click={() => importInput.click()} disabled={importPhase === 'parsing'}>
          {importPhase === 'parsing' ? 'parsing…' : '↓ import GEDCOM →'}
        </button>
        {#if importPhase === 'error'}
          <div class="pill danger">{importMessage}</div>
          {#if importDetail}<p class="dim small">{importDetail}</p>{/if}
        {/if}
      </div>
    </div>

    <div class="card list enter">
      <div class="row head-row">
        <h3>Your trees</h3>
        <span class="chip">{$myTrees.length} total</span>
      </div>
      {#if $myTrees.length === 0}
        <div class="empty">
          <div class="preview">
            <MiniTree rows={4} />
          </div>
          <p class="dim small">
            No trees yet. Create your first one — a minimal pedigree like this
            scaffolds in under a second.
          </p>
        </div>
      {:else}
        <ul class="trees">
          {#each $myTrees as t (t.id)}
            <li class="tree-row">
              <button class="title ghost" on:click={() => go(`/tree/${t.id}`)}>
                <span class="dot" aria-hidden="true"></span>
                <span class="t-name">{t.title}</span>
                <span class="chip {t.visibility === 'public' ? 'gold' : ''}">{t.visibility}</span>
                <span class="chip dim">tree.{short(t.id)}</span>
              </button>
              <div class="meta dim mono small">{new Date(t.updatedAt).toISOString().slice(0, 10)}</div>
              <button class="ghost small" on:click={() => go(`/tree/${t.id}`)}>open →</button>
              <button class="ghost small danger" on:click={() => onDelete(t.id, t.title)} aria-label="Delete">✕</button>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  </section>

  <hr class="rule" />

  <section class="features enter">
    <div class="feat">
      <span class="ico mono">[⌁]</span>
      <h4>Fast by default</h4>
      <p class="dim small">d3-hierarchy SVG, 60fps at 1,000 nodes. No spinner, no skeleton.</p>
    </div>
    <div class="feat">
      <span class="ico mono">[☗]</span>
      <h4>Private by default</h4>
      <p class="dim small">Living people redacted from the public corpus unless consent is recorded.</p>
    </div>
    <div class="feat">
      <span class="ico mono">[⟐]</span>
      <h4>Built to merge</h4>
      <p class="dim small">Auto-resolve the obvious. Surface the genuine conflicts. Keep attribution.</p>
    </div>
    <div class="feat">
      <span class="ico mono">[↯]</span>
      <h4>Open schema</h4>
      <p class="dim small">GEDCOM 5.5.1 in/out. Stable UUIDs. Extend the person record without schema migrations.</p>
    </div>
  </section>
</div>

<style>
  .wrap { max-width: 1040px; margin: 40px auto; padding: 0 24px 96px; }
  .head { margin-bottom: 36px; }
  .head .meta { margin-bottom: 18px; flex-wrap: wrap; }

  h1 { font-size: clamp(34px, 5vw, 54px); letter-spacing: -0.035em; line-height: 1.04; }
  h1 em { color: var(--ls-signal); font-style: italic; font-weight: 400; }
  .lede { font-family: var(--ls-font-display); font-size: var(--ls-step-2); line-height: 1.5; margin: 16px 0 0; max-width: 58ch; color: var(--ls-text-muted); }

  .grid { display: grid; grid-template-columns: minmax(260px, 360px) 1fr; gap: 22px; }
  @media (max-width: 820px) { .grid { grid-template-columns: 1fr; } }

  .head-row { justify-content: space-between; margin-bottom: 8px; }
  .small { font-size: var(--ls-step-0); }

  .trees { list-style: none; padding: 0; margin: 0; }
  .tree-row {
    display: grid;
    grid-template-columns: 1fr auto auto auto; gap: 10px; align-items: center;
    padding: 12px 0;
    border-bottom: 1px solid var(--ls-border);
  }
  .tree-row:last-child { border-bottom: none; }
  .title { display: inline-flex; align-items: center; gap: 10px; padding: 4px 8px; flex-wrap: wrap; }
  .t-name { font-family: var(--ls-font-display); font-weight: 500; font-size: var(--ls-step-2); color: var(--ls-ink); }
  .dot { width: 8px; height: 8px; border-radius: 50%; background: linear-gradient(135deg, var(--ls-accent), var(--ls-forest)); box-shadow: 0 0 10px rgba(216,167,74,.4); }
  .meta { white-space: nowrap; }
  .empty { text-align: center; padding: 12px 8px 4px; }
  .preview {
    max-width: 340px;
    margin: 0 auto 12px;
    padding: 14px 18px;
    border: 1px dashed var(--ls-border);
    border-radius: var(--ls-radius);
    background: var(--ls-bg-2);
  }

  .features {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px;
    margin-top: 4px;
  }
  @media (max-width: 820px) { .features { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 480px) { .features { grid-template-columns: 1fr; } }
  .feat {
    padding: 20px;
    background: var(--ls-surface);
    border: 1px solid var(--ls-border);
    border-radius: var(--ls-radius-lg);
  }
  .feat h4 {
    font-family: var(--ls-font-display);
    margin: 10px 0 6px;
    font-size: var(--ls-step-2);
    color: var(--ls-ink);
    font-weight: 500;
  }
  .feat p { margin: 0; }
  .ico {
    display: inline-block;
    font-size: 14px;
    color: var(--ls-signal);
  }

  .create h3, .list h3 { margin: 0; font-size: var(--ls-step-3); }
  .rule-thin { height: 1px; background: var(--ls-border); margin: 18px -22px; }
</style>
